import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import NewMiniDrawer from "../../components/navMenu/menu-nav";
import freightApi2 from "../../service/modules/freight.api";
import userService from "../../service/user.service";
import FreightFilters from "./components/FreightFilters";
import FreightList from "./components/FreightList";
import FreightDetailPanel from "./components/FreightDetailPanel";
import CarrierHubSummary from "./components/CarrierHubSummary";
import { EMPTY_FILTERS, formatFreightValueDisplay } from "./mockData";
import { isPriceVisibilityMasked } from "../../util/freight/carrierPriceVisibility";
import { markLancesFreightsAsSeen } from "../../util/freight/lancesNewFreightsBadge";
import {
  PAGE_SIZE,
  buildEnterQueuePayload,
  buildLeaveQueuePayload,
  getApiErrorMessage,
  getShipperDisplayName,
  isBidFreight,
  mapAvailableDetail,
  matchesCollectionDateFilter,
  unwrapAvailableDetail,
} from "./availableFreight.mapper";
import {
  EMPTY_HUB_SUMMARY,
  HUB_TAB_AVAILABLE,
  HUB_TAB_IN_REVIEW,
  fetchAllCarrierHubPages,
  filterFreightsByRoute,
  mapCarrierHubListItem,
} from "./carrierHub.mapper";
import "./lances.css";

export default function LancesPage() {
  const isLoggedIn = useSelector((state) => state.usuarioLogado) > 0;
  const user = useSelector((state) => state.user);
  const shipper = useSelector((state) => state.shipper);

  const viewerShipperIds = useMemo(
    () => userService.getShipperIdsFromUser(user),
    [user?.uidShipper, user?.uidShipperAffiliates],
  );

  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [activeTab, setActiveTab] = useState(HUB_TAB_AVAILABLE);
  const [summary, setSummary] = useState({ ...EMPTY_HUB_SUMMARY });
  const [allFreights, setAllFreights] = useState([]);
  const [listTotal, setListTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [page, setPage] = useState(1);
  const [selectedFreightId, setSelectedFreightId] = useState(null);
  const [selectedFreightDetail, setSelectedFreightDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [confirmAcceptFreight, setConfirmAcceptFreight] = useState(null);
  const [acceptingFreightId, setAcceptingFreightId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

  const detailRequestRef = useRef(0);
  const filterLoadingIntentRef = useRef(false);
  const hubRequestRef = useRef(0);

  const shipperDisplayName = useMemo(
    () => getShipperDisplayName(shipper, user?.name || ""),
    [shipper, user?.name],
  );

  const loadHub = useCallback(
    async (activeFilters, tab, { fromFilter = false } = {}) => {
      const requestId = ++hubRequestRef.current;

      if (fromFilter) {
        setFilterLoading(true);
      } else {
        setLoading(true);
      }
      setListError("");

      try {
        const idCarrier = user?.uidShipper || undefined;
        const idUser = user?.uid || undefined;
        if (!idCarrier) {
          if (requestId !== hubRequestRef.current) return;
          setSummary({ ...EMPTY_HUB_SUMMARY });
          setAllFreights([]);
          setListTotal(0);
          setListError(
            "Usuário sem transportadora vinculada. Não é possível carregar o hub.",
          );
          return;
        }

        const { summary: hubSummary, items, pagination } =
          await fetchAllCarrierHubPages(
            (params) => freightApi2.getCarrierHub(params),
            activeFilters,
            { tab, idCarrier, idUser },
          );

        if (requestId !== hubRequestRef.current) return;

        const hasRouteFilter = Boolean(
          activeFilters?.origin?.trim() || activeFilters?.destination?.trim(),
        );
        const hasClientSideFilter =
          hasRouteFilter || Boolean(activeFilters?.collectionDate);

        const mapped = filterFreightsByRoute(
          (items || [])
            .map((item) => mapCarrierHubListItem(item, { viewerShipperIds }))
            .filter((freight) => freight?.id)
            .filter((freight) =>
              matchesCollectionDateFilter(
                freight.collectionDate,
                activeFilters?.collectionDate,
              ),
            ),
          activeFilters,
        );

        setSummary(hubSummary || { ...EMPTY_HUB_SUMMARY });
        setAllFreights(mapped);
        setListTotal(
          hasClientSideFilter
            ? mapped.length
            : typeof pagination?.total === "number"
              ? pagination.total
              : mapped.length,
        );

        const viewerKey = String(user?.uid || user?.uidShipper || "").trim();
        if (viewerKey && tab === HUB_TAB_AVAILABLE) {
          markLancesFreightsAsSeen(
            viewerKey,
            mapped.map((freight) => freight.id),
          );
        }
      } catch (error) {
        if (requestId !== hubRequestRef.current) return;
        console.error("[LancesPage] Erro ao carregar carrier-hub:", error);
        setListError(
          getApiErrorMessage(
            error,
            "Não foi possível carregar os fretes disponíveis.",
          ),
        );
        setSummary({ ...EMPTY_HUB_SUMMARY });
        setAllFreights([]);
        setListTotal(0);
      } finally {
        if (requestId === hubRequestRef.current) {
          setLoading(false);
          setFilterLoading(false);
        }
      }
    },
    [user?.uid, user?.uidShipper, viewerShipperIds],
  );

  const paginatedFreights = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return allFreights.slice(start, start + PAGE_SIZE);
  }, [allFreights, page]);

  const mergedDetailFreight = useMemo(() => {
    if (!selectedFreightId) return null;

    const fromList = allFreights.find((f) => f.id === selectedFreightId);
    if (!selectedFreightDetail && !fromList) return null;

    return {
      ...(fromList ?? {}),
      ...(selectedFreightDetail ?? {}),
      isInCarrierQueue: Boolean(
        selectedFreightDetail?.isInCarrierQueue || fromList?.isInCarrierQueue,
      ),
      isMasked: Boolean(
        selectedFreightDetail?.isMasked ??
          selectedFreightDetail?.priceVisibility?.isMasked ??
          fromList?.isMasked ??
          fromList?.priceVisibility?.isMasked,
      ),
      priceVisibility:
        selectedFreightDetail?.priceVisibility ?? fromList?.priceVisibility,
      freightValue:
        selectedFreightDetail?.priceVisibility?.displayPrice ??
        selectedFreightDetail?.freightValue ??
        fromList?.priceVisibility?.displayPrice ??
        fromList?.freightValue,
      shipper:
        selectedFreightDetail?.priceVisibility?.displayShipperName ??
        selectedFreightDetail?.shipper ??
        fromList?.priceVisibility?.displayShipperName ??
        fromList?.shipper,
      carrierQueue:
        selectedFreightDetail?.carrierQueue ?? fromList?.carrierQueue ?? null,
      carrierQueueJoinedAt:
        selectedFreightDetail?.carrierQueueJoinedAt ??
        fromList?.carrierQueueJoinedAt ??
        selectedFreightDetail?.carrierQueue?.dataHora ??
        fromList?.carrierQueue?.dataHora ??
        null,
      isMyClient: Boolean(
        selectedFreightDetail?.isMyClient ?? fromList?.isMyClient,
      ),
      cargoType: selectedFreightDetail?.cargoType ?? fromList?.cargoType ?? null,
      paymentTermLabel:
        selectedFreightDetail?.paymentTermLabel ??
        fromList?.paymentTermLabel ??
        null,
      contractingType:
        selectedFreightDetail?.contractingType ?? fromList?.contractingType,
      contractingLabel:
        selectedFreightDetail?.contractingLabel ?? fromList?.contractingLabel,
    };
  }, [selectedFreightId, selectedFreightDetail, allFreights]);

  const syncFreightQueueFromDetail = useCallback((freightId, mapped) => {
    if (!freightId || !mapped) return;

    setAllFreights((prev) =>
      prev.map((f) =>
        f.id === freightId
          ? {
              ...f,
              isInCarrierQueue: mapped.isInCarrierQueue,
              carrierQueue: mapped.carrierQueue ?? f.carrierQueue,
              carrierQueueJoinedAt:
                mapped.carrierQueueJoinedAt ??
                mapped.carrierQueue?.dataHora ??
                f.carrierQueueJoinedAt,
            }
          : f,
      ),
    );
  }, []);

  const fetchFreightDetail = useCallback(
    async (freightId) => {
      if (!freightId) {
        setSelectedFreightDetail(null);
        setDetailError("");
        return;
      }

      const requestId = detailRequestRef.current + 1;
      detailRequestRef.current = requestId;
      setDetailLoading(true);
      setDetailError("");
      setSelectedFreightDetail(null);

      try {
        const idCarrier = user?.uidShipper || undefined;
        const idUser = user?.uid || undefined;
        let mapped = null;

        try {
          const hubDetail = await freightApi2.getCarrierHubById(
            freightId,
            idCarrier,
            idUser,
          );
          const rawHub =
            hubDetail?.data?.data ?? hubDetail?.data ?? hubDetail;
          if (rawHub && (rawHub.freightId || rawHub.id)) {
            mapped = mapCarrierHubListItem(
              {
                ...rawHub,
                id: rawHub.freightId || rawHub.id,
              },
              { viewerShipperIds },
            );
            // Preferir mapAvailableDetail para campos ricos do detalhe se o payload for completo
            const asDetail = mapAvailableDetail(rawHub, { viewerShipperIds });
            mapped = { ...asDetail, ...mapped, id: asDetail.id || mapped.id };
          }
        } catch (hubDetailError) {
          // Fallback para available by id enquanto o detail do hub estabiliza
          console.warn(
            "[LancesPage] carrier-hub detail indisponível, usando available:",
            hubDetailError,
          );
        }

        if (!mapped) {
          const response = await freightApi2.getAvailableFreightById(
            freightId,
            idCarrier,
            idUser,
          );
          const raw = unwrapAvailableDetail(response);
          mapped = mapAvailableDetail(raw, { viewerShipperIds });
        }

        if (requestId !== detailRequestRef.current) return;

        setSelectedFreightDetail(mapped);
        syncFreightQueueFromDetail(freightId, mapped);
      } catch (error) {
        if (requestId !== detailRequestRef.current) return;
        console.error("[LancesPage] Erro ao carregar detalhe:", error);
        setDetailError(
          getApiErrorMessage(
            error,
            "Não foi possível carregar os detalhes do frete.",
          ),
        );
      } finally {
        if (requestId === detailRequestRef.current) {
          setDetailLoading(false);
        }
      }
    },
    [user?.uid, user?.uidShipper, viewerShipperIds, syncFreightQueueFromDetail],
  );

  useEffect(() => {
    if (!isLoggedIn) return;
    setPage(1);
    const fromFilter = filterLoadingIntentRef.current;
    filterLoadingIntentRef.current = false;
    loadHub(appliedFilters, activeTab, { fromFilter });
  }, [isLoggedIn, appliedFilters, activeTab, loadHub]);

  useEffect(() => {
    fetchFreightDetail(selectedFreightId);
  }, [selectedFreightId, fetchFreightDetail]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(allFreights.length / PAGE_SIZE));
    if (page > totalPages) setPage(totalPages);
  }, [allFreights.length, page]);

  const handleSearch = () => {
    filterLoadingIntentRef.current = true;
    setAppliedFilters({ ...filters });
    setPage(1);
    setSelectedFreightId(null);
    setSelectedFreightDetail(null);
  };

  const handleClear = () => {
    filterLoadingIntentRef.current = true;
    setFilters({ ...EMPTY_FILTERS });
    setAppliedFilters({ ...EMPTY_FILTERS });
    setPage(1);
    setSelectedFreightId(null);
    setSelectedFreightDetail(null);
  };

  const handleTabChange = (nextTab) => {
    if (!nextTab || nextTab === activeTab) return;
    filterLoadingIntentRef.current = true;
    setActiveTab(nextTab);
    setPage(1);
    setSelectedFreightId(null);
    setSelectedFreightDetail(null);
  };

  const handlePageChange = (nextPage) => {
    const totalPages = Math.max(1, Math.ceil(allFreights.length / PAGE_SIZE));
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
  };

  const handleSelectFreight = (id) => {
    setSelectedFreightId(id);
  };

  const handleCloseDetail = () => {
    setSelectedFreightId(null);
    setSelectedFreightDetail(null);
    setDetailError("");
  };

  const markFreightQueueStatus = useCallback(
    (freightId, isInCarrierQueue) => {
      setAllFreights((prev) =>
        prev.map((f) =>
          f.id === freightId
            ? {
                ...f,
                isInCarrierQueue,
                carrierQueue: isInCarrierQueue ? f.carrierQueue : null,
                carrierQueueJoinedAt: isInCarrierQueue
                  ? f.carrierQueueJoinedAt
                  : null,
              }
            : f,
        ),
      );

      if (selectedFreightId === freightId) {
        setSelectedFreightDetail((prev) =>
          prev
            ? {
                ...prev,
                isInCarrierQueue,
                carrierQueue: isInCarrierQueue ? prev.carrierQueue : null,
                carrierQueueJoinedAt: isInCarrierQueue
                  ? prev.carrierQueueJoinedAt
                  : null,
              }
            : prev,
        );
      }
    },
    [selectedFreightId],
  );

  const refreshHubAfterQueueChange = useCallback(async () => {
    await loadHub(appliedFilters, activeTab, { fromFilter: false });
  }, [loadHub, appliedFilters, activeTab]);

  const handleLeaveQueue = async (freight) => {
    if (!freight?.id || acceptingFreightId) return;

    const freightId = freight.id;
    setAcceptingFreightId(freightId);

    try {
      const payload = buildLeaveQueuePayload(user);
      const response = await freightApi2.leaveTransportQueue(freightId, payload);
      const successMessage =
        response?.data?.message || "Você saiu da fila deste frete.";

      markFreightQueueStatus(freightId, false);
      setSnackbar({
        open: true,
        severity: "success",
        message: successMessage,
      });
      await refreshHubAfterQueueChange();
    } catch (error) {
      console.error("[LancesPage] Erro ao sair da fila:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: getApiErrorMessage(
          error,
          "Não foi possível sair da fila do frete.",
        ),
      });
    } finally {
      setAcceptingFreightId(null);
    }
  };

  const handleAcceptFreight = (freight) => {
    if (!freight || isBidFreight(freight)) return;

    // Enter/leave fila só faz sentido em Disponíveis / Em análise
    const canManageQueue =
      activeTab === HUB_TAB_AVAILABLE || activeTab === HUB_TAB_IN_REVIEW;

    if (!canManageQueue) {
      setSelectedFreightId(freight.id);
      return;
    }

    if (isPriceVisibilityMasked(freight) && !freight.isInCarrierQueue) {
      setSnackbar({
        open: true,
        severity: "info",
        message:
          "Este frete está disponível apenas para visualização (Valor Anunciado).",
      });
      return;
    }

    if (freight.isInCarrierQueue) {
      handleLeaveQueue(freight);
      return;
    }

    if (!user?.uidShipper) {
      setSnackbar({
        open: true,
        severity: "error",
        message:
          "Usuário sem transportadora vinculada. Não é possível entrar na fila.",
      });
      return;
    }

    setConfirmAcceptFreight(freight);
  };

  const handleCancelAccept = () => {
    if (acceptingFreightId) return;
    setConfirmAcceptFreight(null);
  };

  const handleConfirmAccept = async () => {
    if (!confirmAcceptFreight || acceptingFreightId) return;

    const freightId = confirmAcceptFreight.id;
    setAcceptingFreightId(freightId);

    try {
      const payload = buildEnterQueuePayload(user, shipper);
      const response = await freightApi2.enterTransportQueue(freightId, payload);

      const successMessage =
        response?.data?.message ||
        "Você entrou na fila e receberá uma notificação quando o frete for contratado.";

      const joinedAt = new Date().toISOString();

      setAllFreights((prev) =>
        prev.map((f) =>
          f.id === freightId
            ? {
                ...f,
                isInCarrierQueue: true,
                carrierQueueJoinedAt: joinedAt,
                carrierQueue: {
                  inQueue: true,
                  idCarrier: user?.uidShipper || "",
                  nameCarrier: shipperDisplayName,
                  dataHora: joinedAt,
                },
              }
            : f,
        ),
      );

      if (selectedFreightId === freightId) {
        setSelectedFreightDetail((prev) =>
          prev
            ? {
                ...prev,
                isInCarrierQueue: true,
                carrierQueueJoinedAt: joinedAt,
                carrierQueue: {
                  inQueue: true,
                  idCarrier: user?.uidShipper || "",
                  nameCarrier: shipperDisplayName,
                  dataHora: joinedAt,
                },
              }
            : prev,
        );
      }

      setConfirmAcceptFreight(null);
      setSnackbar({
        open: true,
        severity: "success",
        message: successMessage,
      });
      await refreshHubAfterQueueChange();
    } catch (error) {
      console.error("[LancesPage] Erro ao aceitar frete:", error);
      setSnackbar({
        open: true,
        severity: "error",
        message: getApiErrorMessage(
          error,
          "Não foi possível entrar na fila do frete.",
        ),
      });
    } finally {
      setAcceptingFreightId(null);
    }
  };

  if (!isLoggedIn) {
    return <Navigate to="/login" />;
  }

  const acceptValue = confirmAcceptFreight
    ? formatFreightValueDisplay(
        confirmAcceptFreight.freightValue,
        confirmAcceptFreight.pricingType,
      )
    : "";

  return (
    <NewMiniDrawer
      divOpen={
        <Box className="lances-page">
          <Box className="lances-page__header">
            <Typography variant="h5" className="lances-page__title">
              Fretes Disponíveis
            </Typography>
            <Typography variant="body2" className="lances-page__subtitle">
              Central de fretes da transportadora — disponíveis, em análise,
              aceitas, finalizadas e perdidas.
            </Typography>
          </Box>

          <CarrierHubSummary
            summary={summary}
            activeTab={activeTab}
            onSelectTab={handleTabChange}
            loading={loading || filterLoading}
          />

          <Box className="lances-page__layout">
            <Box className="lances-page__main">
              <FreightFilters
                filters={filters}
                onChange={setFilters}
                onSearch={handleSearch}
                onClear={handleClear}
                loading={loading || filterLoading}
              />
              <FreightList
                freights={paginatedFreights}
                page={page}
                totalCount={Math.max(listTotal, allFreights.length)}
                loading={loading && !filterLoading}
                error={listError}
                selectedFreightId={selectedFreightId}
                acceptingFreightId={acceptingFreightId}
                activeTab={activeTab}
                summary={summary}
                onTabChange={handleTabChange}
                onSelectFreight={handleSelectFreight}
                onAcceptFreight={handleAcceptFreight}
                onPageChange={handlePageChange}
              />
            </Box>

            <Box className="lances-page__sidebar">
              <FreightDetailPanel
                freight={mergedDetailFreight}
                loading={detailLoading && !mergedDetailFreight}
                error={detailError}
                acceptingFreightId={acceptingFreightId}
                activeTab={activeTab}
                onClose={handleCloseDetail}
                onAcceptFreight={handleAcceptFreight}
              />
            </Box>
          </Box>

          <Backdrop
            open={filterLoading}
            sx={{
              color: "#fff",
              zIndex: (theme) => theme.zIndex.modal + 1,
              backgroundColor: "rgba(1, 36, 67, 0.35)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <CircularProgress color="inherit" size={42} />
              <Typography variant="body2" sx={{ color: "#fff", fontWeight: 500 }}>
                Atualizando fretes...
              </Typography>
            </Box>
          </Backdrop>

          <Dialog
            open={Boolean(confirmAcceptFreight)}
            onClose={handleCancelAccept}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Confirmar entrada na fila do frete</DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  Você está prestes a entrar na fila do frete{" "}
                  <strong>{confirmAcceptFreight?.number}</strong> por{" "}
                  <strong>{acceptValue}</strong>.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  O frete ficará vinculado ao embarcador{" "}
                  <strong>{shipperDisplayName}</strong> caso seja escolhido.
                </Typography>
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleCancelAccept}
                disabled={Boolean(acceptingFreightId)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                className="lances-btn-search"
                onClick={handleConfirmAccept}
                disabled={Boolean(acceptingFreightId)}
              >
                {acceptingFreightId ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Confirmar entrada na fila"
                )}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={snackbar.severity}
              onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      }
    />
  );
}
